/**
 * Theme system definition for LangWatch.
 * This file only exports the Chakra UI `system` — the app shell (providers,
 * routing, NProgress) now lives in src/AppProviders.tsx and src/main.tsx.
 */
import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
  mergeConfigs,
} from "@chakra-ui/react";
import { avatarMarksConfig } from "../theme/avatarMarks";
import { ledgerRecipesConfig } from "../theme/ledgerRecipes";
import { ledgerThemeConfig } from "../theme/ledgerTheme";

// The Ledger faces (Source Sans 3, Source Code Pro) are self-hosted through
// @fontsource-variable and imported in main.tsx; ledgerTheme points Chakra's
// `fonts` tokens at them. Colours, sizes, radii and the body come from
// ledgerTheme.ts (src/styles/tokens.css); `color-scheme` comes from
// tokens.css, which declares it per theme so the browser's own scrollbars,
// spinners and popups follow the app's theme.

const appConfig = defineConfig({
  globalCss: {
    "*::selection": {
      // Chakra by default overrides browser selection color, I really don't like things overriding defaults
      // @ts-expect-error
      bg: null,
    },
    // Chakra's `CodeBlock` paints highlighted lines via an absolutely
    // positioned `::after` pseudo on `[data-line][data-highlight]`,
    // backed by the `--highlight-bg` custom property and a hardcoded
    // gray inline-start border. Override both globally so every code
    // block (env-block in onboarding, pinned attributes in the trace
    // drawer, …) lights up in the accent.
    "[data-line][data-highlight], [data-line][data-diff]": {
      "--highlight-bg": "var(--color-accent-soft)",
    },
    "[data-line][data-highlight]::after, [data-line][data-diff]::after": {
      borderInlineStartColor: "var(--color-accent) !important",
      background: "var(--color-accent-soft) !important",
    },
  },
  theme: {
    tokens: {
      // TODO: those are not working, we need to manually override cursors below
      cursor: {
        button: {
          value: "pointer",
        },
        menuitem: {
          value: "pointer",
        },
        checkbox: {
          value: "pointer",
        },
        radio: {
          value: "pointer",
        },
        slider: {
          value: "pointer",
        },
        switch: {
          value: "pointer",
        },
        option: {
          value: "pointer",
        },
      },
    },
    recipes: {
      heading: defineRecipe({
        base: {
          fontWeight: "500",
          color: "fg",
        },
        variants: {
          size: {
            md: { textStyle: "md" },
            lg: { textStyle: "2xl" },
          },
        },
        defaultVariants: {
          size: "md",
        },
      }),
      link: defineRecipe({
        base: {
          focusRing: "none",
        },
      }),
    },
    slotRecipes: {
      accordion: defineSlotRecipe({
        slots: ["itemTrigger"],
        base: {
          root: {
            width: "full",
          },
          item: {
            borderRadius: "lg",
          },
          itemTrigger: {
            cursor: "pointer",
            _hover: {
              bg: "surface.2",
            },
          },
        },
      }),
    },
  },
});

/**
 * The app's system: Chakra's defaults, the app's few remaining recipe
 * tweaks, then the Ledger. `ledgerThemeConfig` points Chakra's token names at
 * src/styles/tokens.css, `ledgerRecipesConfig` replaces Chakra's primitive
 * and form recipes, and `avatarMarksConfig` adds the avatar marks' own hues.
 * The raw colour ramps are Chakra's own (the app no longer overrides them):
 * nothing in app code reads a ramp step, and every palette app code names is
 * re-pointed at the tokens.
 */
export const system = createSystem(
  defaultConfig,
  mergeConfigs(
    appConfig,
    ledgerThemeConfig,
    ledgerRecipesConfig,
    avatarMarksConfig,
  ),
);

// The LangWatch app shell (providers, routing, NProgress) has moved to:
// - src/AppProviders.tsx (provider hierarchy)
// - src/main.tsx (entry point with RouterProvider)
// - src/routes.tsx (route definitions)
